import { useState, useEffect, useRef } from "react";
import { formatHoursMinutes } from "@/lib/video-metadata";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput, { getPhoneMask } from "@/components/PhoneInput";
import { COUNTRIES, getCountryCodeByName, getDefaultCountryByBrowserLanguage } from "@/constants/countries";
import { JOB_TITLES } from "@/constants/job-titles";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  User,
  Mail,
  Crown,
  Clock,
  Video,
  Instagram,
  Music2,
  Youtube,
  Linkedin,
  Facebook,
  X,
  Edit,
  Users,
  Plus,
  Settings,
  Lock,
  ChevronDown,
  Copy,
  Check,
  Trash2,
  LogOut,
  Upload,
  Image as ImageIcon,
  RefreshCw,
  QrCode,
  Wallet,
  Sparkles,
  FileOutput
} from "lucide-react";
import PixRenewalModal from "@/components/PixRenewalModal";
import { BotConnectionSettings } from "@/components/BotConnectionSettings";
import { ObjectiveSwitchConfirmDialog } from "@/components/ObjectiveSwitchConfirmDialog";
import { resolveClipperMonthlyClipLimit } from "@/lib/onboarding/resolveClipperMonthlyClipLimit";
import { useAuth } from "@/contexts/AuthContext";
import { authAxios } from "@/lib/axios";
import { useMyDataQuery, useUpdateMyDataMutation, useLeaveTeamMutation, useRemoveTeamAccessMutation, useSetCancellationReasonMutation, useSetPasswordMutation, useChangeOnboardingObjectiveMutation } from "@/generated/graphql";
import { useMutation, useQuery } from "@apollo/client";
import {
  DISCONNECT_MY_SOCIAL_CONNECTION,
  MY_SOCIAL_CONNECTIONS,
  UPDATE_TEAM_ROLE,
  REFRESH_INVITATION_TOKEN,
} from "@/queries/queries";
import { createWhatsAppUrl } from "@/constants/contact";
import { ROLES, getRoleLabel, getRoleDescription, canManageRoles, canInviteUsers } from "@/constants/roles";
import { useHasFeature } from "@/lib/useHasFeature";
import { SOCIAL_PUBLISHING_FEATURE } from "@/lib/publishing/socialPublishingFeature";
import { useSocialLinkingPlatforms } from "@/hooks/use-social-linking-platforms";
import { useSocialPostingPlatforms } from "@/hooks/use-social-posting-platforms";
import type { SocialLinkingPlatform } from "@/lib/account-settings/visibleSocialLinkingPlatforms";
import { socialAccountName } from "@/lib/account-settings/socialAccountName";
import { publishReconnectNoticeKey, shouldShowPublishReconnectNotice } from "@/lib/account-settings/shouldShowPublishReconnectNotice";
import { isPaidSubscription } from "@/lib/quota/isPaidSubscription";
import { isSubscriptionPastDue } from "@/lib/billing/isSubscriptionPastDue";
import { isCanceledSubscription } from "@/lib/billing/isCanceledSubscription";
import { resolvePastDueRecovery } from "@/lib/billing/resolvePastDueRecovery";
import { resolveWalletRenewalEligibility } from "@/lib/billing/resolveWalletRenewalEligibility";
import { startStripeCustomerPortal } from "@/lib/billing/startStripeCustomerPortal";
import { isLiveStorePlan, storeManageUrl, storeProviderOf } from "@/lib/billing/storeBilling";
import { PlanRenewalDate } from "@/components/billing/PlanRenewalDate";
import { RenewedCycleNotice } from "@/components/billing/RenewedCycleNotice";
import { useToast } from "@/hooks/use-toast";
import { LeaderboardAnonymousToggle } from "@/components/cpm/LeaderboardAnonymousToggle";
import { useUploadFile, UploadProgress } from "@/hooks/use-upload-file";
import type { AccountSettingsSocialPlatform } from "@/lib/account-settings/openAccountSettingsEvent";
import { useSocialAccountConnection } from '@/hooks/use-social-account-connection';

interface AccountSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  focusSocialPlatform?: AccountSettingsSocialPlatform | null;
  focusRequestKey?: number;
}

type SocialPlatform = SocialLinkingPlatform;

interface SocialConnection {
  id: string;
  platform: SocialPlatform;
  username: string;
  display_name: string;
  avatar_url?: string | null;
  status: string;
  scopes: string[];
}

const detectCountryFromPhone = (phoneNumber: string) => {
  if (!phoneNumber) return "Brasil";

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Find country by phone code
  for (const country of COUNTRIES) {
    const countryCodeDigits = country.phoneCode.replace('+', '');
    if (digits.startsWith(countryCodeDigits)) {
      return country.name;
    }
  }

  return "Brasil"; // Default fallback
};

const removeCountryCodeFromPhone = (phoneNumber: string, country: string) => {
  if (!phoneNumber) return "";

  const countryCode = getCountryCodeByName(country);
  const countryCodeDigits = countryCode.replace('+', '');

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // If phone starts with country code, remove it
  if (digits.startsWith(countryCodeDigits)) {
    return digits.substring(countryCodeDigits.length);
  }

  return phoneNumber;
};

const formatPhoneWithMask = (value: string, country: string) => {
  const digits = value.replace(/\D/g, '');
  const mask = getPhoneMask(country);
  let result = '';
  let digitIndex = 0;

  for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
    if (mask[i] === '9') {
      result += digits[digitIndex];
      digitIndex++;
    } else {
      result += mask[i];
    }
  }

  return result;
};

const formatPhoneForAPI = (phoneValue: string, country: string) => {
  if (!phoneValue) return "";

  // Remove all non-digit characters from the phone value
  const digits = phoneValue.replace(/\D/g, '');

  // Get the country code
  const countryCode = getCountryCodeByName(country);
  const countryCodeDigits = countryCode.replace('+', '');

  // If the phone number already starts with the country code, return as is
  if (digits.startsWith(countryCodeDigits)) {
    return `+${digits}`;
  }

  // Otherwise, prepend the country code
  return `+${countryCodeDigits}${digits}`;
};

const CANCELLATION_REASON_KEYS = [
  "notExpected",
  "tooExpensive",
  "noTime",
  "technicalProblems",
  "foundBetterTool",
  "other"
] as const;



const AccountSettings = ({
  isOpen,
  onClose,
  focusSocialPlatform = null,
  focusRequestKey = 0,
}: AccountSettingsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { changePassword } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] = useState(false);
  const [isLeaveTeamModalOpen, setIsLeaveTeamModalOpen] = useState(false);
  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);
  const [objectiveSwitchTarget, setObjectiveSwitchTarget] = useState<'own_brand' | 'other_channels' | null>(null);
  const [userToRemove, setUserToRemove] = useState<{ email: string; name: string } | null>(null);
  const [isRegenerateTokenModalOpen, setIsRegenerateTokenModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [, setUploadProgress] = useState<UploadProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasInvitationFeature = useHasFeature("team");
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  const linkingPlatforms = useSocialLinkingPlatforms();
  const canPostToFacebook = useSocialPostingPlatforms().includes('facebook');
  const [isChangingProfilePicture, setIsChangingProfilePicture] = useState(false);
  const [loadingManagePlan, setLoadingManagePlan] = useState(false);
  const [isPixRenewalModalOpen, setIsPixRenewalModalOpen] = useState(false);
  const [renewalModalVariant, setRenewalModalVariant] = useState<'pix' | 'grant'>('pix');
  const [disconnectTarget, setDisconnectTarget] = useState<SocialConnection | null>(null);
  const socialProviderRefs = useRef<Partial<Record<SocialPlatform, HTMLDivElement | null>>>({});

  // Apollo GraphQL hooks
  const { data: myData, loading: dataLoading, refetch } = useMyDataQuery();
  const [setPassword] = useSetPasswordMutation();
  const [updateMyData, { loading: updateLoading }] = useUpdateMyDataMutation();
  const [leaveTeam, { loading: leaveTeamLoading }] = useLeaveTeamMutation();
  const [removeTeamAccess, { loading: removeTeamLoading }] = useRemoveTeamAccessMutation();
  const [setCancellationReason, { loading: cancellationLoading }] = useSetCancellationReasonMutation();
  const [changeOnboardingObjective, { loading: objectiveSwitchLoading }] = useChangeOnboardingObjectiveMutation();
  const [updateTeamRole, { loading: updateTeamRoleLoading }] = useMutation(UPDATE_TEAM_ROLE);
  const [refreshInvitationToken, { loading: refreshInvitationTokenLoading }] = useMutation(REFRESH_INVITATION_TOKEN);
  const {
    data: socialConnectionsData,
    loading: socialConnectionsLoading,
    refetch: refetchSocialConnections,
  } = useQuery(MY_SOCIAL_CONNECTIONS, {
    skip: !isOpen,
    fetchPolicy: "network-only",
  });
  const [disconnectSocialConnection, { loading: disconnectingSocial }] = useMutation(
    DISCONNECT_MY_SOCIAL_CONNECTION,
  );
  const { connect: handleConnectSocial, connectingPlatform } = useSocialAccountConnection({
    enabled: isOpen, onRefresh: refetchSocialConnections,
  });
  // Upload file hook
  const { getUploadUrl, isLoading: isUploading, error: uploadError, createBlobUrl, revokeBlobUrl } = useUploadFile();

  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    country: getDefaultCountryByBrowserLanguage(),
    instagram: "",
    tiktok: "",
    youtube: "",
    jobTitle: "",
    profilePicture: ""
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [cancellationData, setCancellationData] = useState({
    reason: "",
    customReason: "",
    feedback: ""
  });

  const [emailOptIn, setEmailOptIn] = useState(true);

  // Initialize profile data from GraphQL query
  useEffect(() => {
    if (myData?.myData) {
      const userData = myData.myData;
      const detectedCountry = detectCountryFromPhone(userData.phone_number || "");
      const phoneWithoutCode = removeCountryCodeFromPhone(userData.phone_number || "", detectedCountry);
      const formattedPhone = formatPhoneWithMask(phoneWithoutCode, detectedCountry);

      setProfileData({
        name: userData.name || "",
        email: userData.email || "",
        phone: formattedPhone,
        jobTitle: userData.job_title || "clipping-channel",
        profilePicture: userData.profile_picture || "",
        country: detectedCountry,
        // These fields are not in the GraphQL schema yet
        instagram: "",
        tiktok: "",
        youtube: "",
      });

      setEmailOptIn(userData.email_marketing_opt_in !== false);
    }
  }, [myData]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore body overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !focusSocialPlatform || socialConnectionsLoading) return;
    const provider = socialProviderRefs.current[focusSocialPlatform];
    if (!provider) return;
    provider.focus({ preventScroll: true });
    provider.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusRequestKey, focusSocialPlatform, isOpen, socialConnectionsLoading]);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleDisconnectSocial = async () => {
    if (!disconnectTarget) return;
    try {
      await disconnectSocialConnection({ variables: { id: disconnectTarget.id } });
      setDisconnectTarget(null);
      await refetchSocialConnections();
      toast({ title: t("account.socialDisconnected") });
    } catch (error: any) {
      toast({
        title: t("account.socialDisconnectFailed"),
        description: error?.message,
        variant: "destructive",
      });
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleCancellationChange = (field: string, value: string) => {
    setCancellationData(prev => ({ ...prev, [field]: value }));
  };


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast({
        title: t('account.formatNotSupported'),
        description: t('account.onlyJpgPng'),
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t('account.fileTooLarge'),
        description: t('account.maxSize'),
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const blobUrl = createBlobUrl(file);
    setPreviewUrl(blobUrl);
    setIsProfilePictureModalOpen(true);
  };

  const handleProfilePictureUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsChangingProfilePicture(true);
      const uploadUrl = await getUploadUrl(selectedFile, setUploadProgress);

      if (uploadUrl) {
        // Update profile with new picture URL
        await updateMyData({
          variables: {
            name: profileData.name || undefined,
            phoneNumber: formatPhoneForAPI(profileData.phone, profileData.country),
            profilePicture: {
              url: uploadUrl,
              type: selectedFile.type,
              size: selectedFile.size,
            } as any,
          }
        });

        toast({
          title: t('account.profilePictureUpdated'),
          description: t('account.profilePictureUpdatedDesc'),
        });

        setProfileData(prev => ({ ...prev, profilePicture: uploadUrl }));
        handleCloseProfilePictureModal();
      }
    } catch (error: any) {
      console.error('Failed to upload profile picture:', error);
      toast({
        title: t('account.uploadError'),
        description: error?.message || t('account.uploadError'),
        variant: "destructive",
      });
    } finally {
      setIsChangingProfilePicture(false);
    }
  };

  const handleCloseProfilePictureModal = () => {
    setIsProfilePictureModalOpen(false);
    setSelectedFile(null);
    setUploadProgress(null);

    if (previewUrl) {
      revokeBlobUrl(previewUrl);
      setPreviewUrl("");
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveProfile = async () => {
    if (!myData?.myData) return;

    setIsLoading(true);
    try {
      // Format the phone number properly for the API
      const formattedPhoneNumber = formatPhoneForAPI(profileData.phone, profileData.country);

      await updateMyData({
        variables: {
          name: profileData.name || undefined,
          phoneNumber: formattedPhoneNumber,
          // Note: job_title, instagram, tiktok, youtube are not in the GraphQL mutation yet
          // They would need to be added to the schema and mutation
          emailMarketingOptIn: emailOptIn,
        }
      });

      toast({
        title: t('account.profileUpdated'),
        description: t('account.profileUpdatedDesc'),
      });

      // Refetch the data to get the latest values
      await refetch();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast({
        title: t('account.profileUpdateError'),
        description: t('account.profileUpdateErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleObjectiveSwitch = async (to: 'own_brand' | 'other_channels') => {
    const home = to === 'own_brand' ? '/projects' : '/community-library';
    const errorKey = to === 'own_brand'
      ? 'account.switchToOwnContent.error'
      : 'account.switchToClippingChannels.error';
    try {
      await changeOnboardingObjective({ variables: { to } });
      await refetch();
      setObjectiveSwitchTarget(null);
      onClose();
      navigate(home);
    } catch (error) {
      const isInvalidObjectiveChange = error instanceof Error
        && error.message.includes('invalid_objective_change');
      if (isInvalidObjectiveChange) {
        const refetched = await refetch();
        if (refetched.data?.myData?.onboarding_objective === to) {
          setObjectiveSwitchTarget(null);
          onClose();
          navigate(home);
          return;
        }
      }
      toast({ title: t(errorKey), variant: 'destructive' });
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: t('account.passwordMismatch'),
        variant: "destructive",
      });
      return;
    }

    const isCreatingPassword = myData?.myData?.hasPassword === false;

    setIsPasswordLoading(true);
    try {
      if (isCreatingPassword) {
        await setPassword({
          variables: {
            newPassword: passwordData.newPassword,
          },
        });
        toast({
          title: t('auth.passwordChanged'),
          description: t('auth.passwordChangedDescription'),
        });
        await refetch();
      } else {
        await changePassword(passwordData.currentPassword, passwordData.newPassword);
      }

      // Clear password fields after successful change
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setIsPasswordOpen(false);
    } catch (error) {
      console.error('Failed to change password:', error);
      if (isCreatingPassword) {
        toast({
          title: t('auth.passwordChangeError'),
          variant: "destructive",
        });
      }
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleCancellationSubmit = async () => {
    // Validation: reason and feedback are always required
    // For "other", customReason is also required
    if (!cancellationData.reason || !cancellationData.feedback ||
        (cancellationData.reason === "other" && !cancellationData.customReason)) {
      toast({
        title: t('checkout.requiredField'),
        description: t('checkout.fillDocumentField'),
        variant: "destructive",
      });
      return;
    }

    try {
      // For "other", use the custom reason; otherwise use the translated reason
      const reasonToSubmit = cancellationData.reason === "other"
        ? cancellationData.customReason
        : t(`account.cancellationReasons.${cancellationData.reason}`);

      await setCancellationReason({
        variables: {
          reason: reasonToSubmit,
          feedback: cancellationData.feedback
        }
      });

      toast({
        title: t('account.feedbackSent'),
        description: t('account.feedbackSentDesc'),
      });

      // Close the modal and redirect to WhatsApp
      setIsCancellationModalOpen(false);
      onClose();

      const url = createWhatsAppUrl(t('accountSettings.cancelWhatsappMessage'));
      window.open(url, '_blank');

    } catch (error: any) {
      console.error('Failed to submit cancellation reason:', error);
      toast({
        title: t('account.feedbackError'),
        description: error?.message || t('account.feedbackError'),
        variant: "destructive",
      });
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to get subscription data
  const getSubscriptionData = () => {
    if (!myData?.myData?.subscription) return null;

    try {
      const subscription = typeof myData.myData.subscription === 'string'
        ? JSON.parse(myData.myData.subscription)
        : myData.myData.subscription;

      return subscription;
    } catch (error) {
      console.error('Error parsing subscription data:', error);
      return null;
    }
  };

  // Helper function to format cancellation date in Brazilian Portuguese
  const formatCancellationDate = (dateString: string) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const subscriptionData = getSubscriptionData();
  const isFreePlan = !isPaidSubscription(subscriptionData);
  const socialConnections = (
    socialConnectionsData?.mySocialConnections || []
  ) as SocialConnection[];

  const isStripePlan = subscriptionData?.payment_provider === 'stripe';
  const storeProvider = isLiveStorePlan(subscriptionData) ? storeProviderOf(subscriptionData) : null;
  const isPastDue = isSubscriptionPastDue(subscriptionData);
  const isCanceledPlan = isCanceledSubscription(subscriptionData);
  const walletRenewalEligibility = resolveWalletRenewalEligibility({
    subscription: subscriptionData,
    now: new Date(),
  });
  const isWalletGrantEligible = walletRenewalEligibility.ok
    && walletRenewalEligibility.path === 'grant';

  // Check if current user is the plan master
  const isPlanMaster = myData?.myData?.email === subscriptionData?.master?.email;

  const handleLeaveTeam = async () => {
    try {
      await leaveTeam();

      toast({
        title: t('account.leftTeam'),
        description: t('account.leftTeamDesc'),
      });

      // Refetch myData to update the UI
      await refetch();
    } catch (error: any) {
      console.error('Error leaving team:', error);
      toast({
        title: t('account.leaveTeamError'),
        description: error?.message || t('account.leaveTeamError'),
        variant: "destructive",
      });
    }
  };

  const handleRemoveUser = async (userEmail: string) => {
    try {
      await removeTeamAccess({
        variables: { email: userEmail }
      });

      toast({
        title: t('account.userRemoved'),
        description: t('account.userRemovedDesc'),
      });

      // Refetch myData to update the users list
      await refetch();
    } catch (error: any) {
      console.error('Error removing user:', error);
      toast({
        title: t('account.removeUserError'),
        description: error?.message || t('account.removeUserError'),
        variant: "destructive",
      });
    }
  };

  const handleChangeUserRole = async (userEmail: string, newRole: string) => {
    try {
      await updateTeamRole({
        variables: { email: userEmail, role: newRole }
      });

      toast({
        title: t('account.roleChanged'),
        description: t('account.roleChangedDesc', { role: getRoleLabel(newRole) }),
      });

      // Refetch myData to update the users list
      await refetch();
    } catch (error: any) {
      console.error('Error changing user role:', error);
      toast({
        title: t('account.roleChangeError'),
        description: error?.message || t('account.roleChangeError'),
        variant: "destructive",
      });
    }
  };

  const handleRegenerateInvitationToken = async () => {
    try {
      await refreshInvitationToken();

      toast({
        title: t('account.invitationRegenerated'),
        description: t('account.invitationRegeneratedDesc'),
      });

      // Refetch myData to get the new invitation token
      await refetch();
      setIsRegenerateTokenModalOpen(false);
    } catch (error: any) {
      console.error('Error regenerating invitation token:', error);
      toast({
        title: t('account.invitationRegenerateError'),
        description: error?.message || t('account.invitationRegenerateError'),
        variant: "destructive",
      });
    }
  };

  // Get current user's role from subscription data
  const getCurrentUserRole = (): string | undefined => {
    if (!myData?.myData?.email || !subscriptionData?.users) return undefined;
    const currentUser = subscriptionData.users.find((u: any) => u.email === myData.myData?.email);
    return currentUser?.role;
  };

  const currentUserRole = getCurrentUserRole();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Show loading state while data is being fetched
  if (dataLoading) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>{t('common.loadingData')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
      <Card
        className="w-full max-w-6xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
          <CardTitle className="text-2xl font-bold">{t('account.title')}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* Profile Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* User Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={profileData.profilePicture || ""} />
                  <AvatarFallback className="bg-gradient-primary text-primary-foreground text-xl">
                    {profileData.name ? getInitials(profileData.name) : <User className="w-8 h-8" />}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold">{profileData.name || t('account.loading')}</h2>
                  <p className="text-muted-foreground">{t(JOB_TITLES.find(j => j.value === profileData.jobTitle)?.labelKey || 'jobTitles.clippingChannel')}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className={`${isFreePlan ? "bg-white/10 text-white" : "bg-primary/10 text-primary"}`}>
                      {isFreePlan ? null : <Crown className="w-3 h-3 mr-1" />}
                      {t('account.plan')} {subscriptionData?.plan?.name || t('common.free')}
                    </Badge>
                    {myData?.myData?.provider === 'microsoft' && (
                      <Badge variant="secondary" className="bg-white/10 text-white flex items-center gap-1">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 23 23"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect x="0" y="0" width="10" height="10" fill="#F25022" />
                          <rect x="13" y="0" width="10" height="10" fill="#7FBA00" />
                          <rect x="0" y="13" width="10" height="10" fill="#00A4EF" />
                          <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
                        </svg>
                        {t('account.microsoftAccess')}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="ml-auto">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {t('account.editPhoto')}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Form Fields */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">{t('account.fullName')}</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <PhoneInput
                  value={profileData.phone}
                  onChange={(value) => handleInputChange("phone", value)}
                  country={profileData.country}
                  onCountryChange={(country) => handleInputChange("country", country)}
                  label={t('common.phone')}
                />
                <div className="grid gap-2">
                  <Label htmlFor="jobTitle">{t('account.jobTitle')}</Label>
                  <select
                    id="jobTitle"
                    value={profileData.jobTitle}
                    disabled
                    onChange={(e) => handleInputChange("jobTitle", e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {JOB_TITLES.map((jobTitle) => (
                      <option key={jobTitle.value} value={jobTitle.value}>
                        {t(jobTitle.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Separator />

              {/* Email Notifications Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {t('account.notifications')}
                </h3>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="email_marketing_opt_in"
                    checked={emailOptIn}
                    onCheckedChange={(checked) => setEmailOptIn(checked === true)}
                  />
                  <Label htmlFor="email_marketing_opt_in" className="cursor-pointer">
                    {t('accountSettings.emailMarketingOptIn')}
                  </Label>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {t('cpmLeaderboard.title')}
                </h3>
                <LeaderboardAnonymousToggle variant="settings" />
              </div>

              {/* Password Section - Local accounts may also be linked to Google */}
              {myData?.myData?.provider === 'local' && (
                <>
                  <Separator />

                  <Collapsible open={isPasswordOpen} onOpenChange={setIsPasswordOpen}>
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Lock className="w-5 h-5" />
                          {myData.myData.hasPassword === false
                            ? t('account.setPassword')
                            : t('account.changePassword')}
                        </h3>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isPasswordOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-4 pt-4">
                      <div className="grid gap-4">
                        {myData.myData.hasPassword === false && (
                          <p className="text-sm text-muted-foreground">
                            {t('account.setPasswordDescription')}
                          </p>
                        )}
                        {myData.myData.hasPassword !== false && (
                          <div className="grid gap-2">
                            <Label htmlFor="currentPassword">{t('account.currentPassword')}</Label>
                            <Input
                              id="currentPassword"
                              type="password"
                              value={passwordData.currentPassword}
                              onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
                            />
                          </div>
                        )}
                        <div className="grid gap-2">
                          <Label htmlFor="newPassword">{t('account.newPassword')}</Label>
                          <Input
                            id="newPassword"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="confirmPassword">{t('account.confirmNewPassword')}</Label>
                          <Input
                            id="confirmPassword"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                          />
                        </div>
                        <Button
                          onClick={handleChangePassword}
                          disabled={
                            isPasswordLoading ||
                            (myData.myData.hasPassword !== false && !passwordData.currentPassword) ||
                            !passwordData.newPassword ||
                            !passwordData.confirmPassword
                          }
                          className="w-full"
                        >
                          {isPasswordLoading
                            ? t('account.changing')
                            : myData.myData.hasPassword === false
                              ? t('account.setPasswordButton')
                              : t('account.changePasswordButton')}
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Separator />
                </>
              )}

              {/* Social Media Connections */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    {t('account.connectSocialMedia')}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('account.socialConnectionsDescription')}
                  </p>
                </div>

                <div className="grid gap-4">
                  {([
                    {
                      platform: "instagram" as const,
                      label: t("account.instagram"),
                      connectLabel: t("account.connectInstagram"),
                      icon: <Instagram className="w-6 h-6 text-pink-500" />,
                      note: t("account.instagramProfessionalRequired"),
                    },
                    {
                      platform: "tiktok" as const,
                      label: t("account.tiktok"),
                      connectLabel: t("account.connectTikTok"),
                      icon: <Music2 className="w-6 h-6 text-foreground" />,
                      note: t("account.tiktokConnectionNote"),
                    },
                    {
                      platform: "youtube" as const,
                      label: t("account.youtube"),
                      connectLabel: t("account.connectYouTube"),
                      icon: <Youtube className="w-6 h-6 text-red-500" />,
                      note: t("account.youtubeConnectionNote"),
                    },
                    {
                      platform: "linkedin" as const,
                      label: t("account.linkedin"),
                      connectLabel: t("account.connectLinkedIn"),
                      icon: <Linkedin className="w-6 h-6 text-[#0A66C2]" />,
                      note: t("account.linkedinConnectionNote"),
                    },
                    {
                      platform: "facebook" as const,
                      label: t("account.facebook"),
                      connectLabel: t("account.connectFacebook"),
                      icon: <Facebook className="w-6 h-6 text-[#1877F2]" />,
                      note: t("account.facebookConnectionNote"),
                    },
                  ].filter((item) => linkingPlatforms.includes(item.platform)
                    // A user who can no longer link a platform (Facebook before
                    // Meta approval) still sees and can disconnect what they linked.
                    || socialConnections.some((candidate) => candidate.platform === item.platform))).map((item) => {
                    const platformConnections = socialConnections.filter(
                      (candidate) => candidate.platform === item.platform,
                    );
                    const canLink = linkingPlatforms.includes(item.platform);
                    return (
                      <div
                        key={item.platform}
                        ref={(node) => {
                          socialProviderRefs.current[item.platform] = node;
                        }}
                        tabIndex={-1}
                        data-testid={`social-provider-${item.platform}`}
                        className="space-y-3 rounded-lg border p-4 focus:outline-none focus:ring-2 focus:ring-primary/60"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            {item.icon}
                            <div className="min-w-0">
                              <p className="font-medium">{item.label}</p>
                              <p className="text-sm text-muted-foreground">{item.note}</p>
                            </div>
                          </div>
                          {canLink && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => void handleConnectSocial(item.platform)}
                              disabled={
                                socialConnectionsLoading
                                || connectingPlatform === item.platform
                              }
                            >
                              <Plus className="w-4 h-4" />
                              {platformConnections.length > 0
                                ? t("account.addAnotherSocialAccount")
                                : item.connectLabel}
                            </Button>
                          )}
                        </div>

                        {platformConnections.map((connection) => (
                          <div
                            key={connection.id}
                            className="flex flex-col gap-3 rounded-md bg-muted/40 p-3 sm:flex-row sm:flex-wrap sm:items-center"
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-3">
                              {connection.avatar_url ? (
                                <Avatar className="h-10 w-10">
                                  <AvatarImage src={connection.avatar_url} />
                                  <AvatarFallback>{item.icon}</AvatarFallback>
                                </Avatar>
                              ) : item.icon}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="truncate font-medium">
                                    {socialAccountName(connection)}
                                  </span>
                                  {connection.status === "active" && (
                                    <Badge variant="secondary" className="text-emerald-500">
                                      {t("account.socialConnectedBadge")}
                                    </Badge>
                                  )}
                                </div>
                                <p className="truncate text-sm text-muted-foreground">
                                  {connection.display_name || connection.username}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => void handleConnectSocial(item.platform)}
                                disabled={connectingPlatform === item.platform}
                              >
                                <RefreshCw className="w-4 h-4" />
                                {t("account.reconnectSocial")}
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => setDisconnectTarget(connection)}
                              >
                                {t("account.disconnectSocial")}
                              </Button>
                            </div>
                            {shouldShowPublishReconnectNotice(connection, { hasSocialPublishing, canPostToFacebook }) && (
                              <div
                                data-testid={`${connection.platform}-reconnect-notice`}
                                className="mt-2 w-full rounded-md border border-amber-500/40 bg-amber-500/10 p-2"
                              >
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                  {connection.platform === 'facebook' && (
                                    <strong className="block">{t('account.facebookReconnectTitle')}</strong>
                                  )}
                                  {t(publishReconnectNoticeKey(connection.platform))}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Plan Info */}
            <div className="space-y-6">
              <Card className={isPastDue ? "border-red-500" : "border-primary/20"}>
                <CardHeader>
                  <CardTitle className={`text-lg flex items-center gap-2 ${isPastDue ? 'text-red-600' : ''}`}>
                    {isFreePlan ? null : (
                      <Crown className={`w-5 h-5 ${isPastDue ? 'text-red-500' : 'text-primary'}`} />
                    )}
                    {t('account.myPlan')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <Badge
                      variant="secondary"
                      className={`text-lg px-4 py-2 ${
                        isPastDue
                          ? 'bg-red-600 text-white'
                          : 'bg-primary/10 text-primary'
                      }`}
                    >
                      {t('account.plan')} {subscriptionData?.plan?.name || "Gold"}
                    </Badge>
                    {isPastDue && (
                      <div className="mt-3 p-2 border border-red-500 bg-red-50 rounded-lg">
                        <p className="text-sm text-red-600 font-semibold">
                          {t('account.pastDue')}
                        </p>
                        {subscriptionData?.cancellation_at && (
                          <p className="text-xs text-red-600 mt-1">
                            {t('account.pastDueGrace', {
                              date: formatCancellationDate(subscriptionData.cancellation_at),
                            })}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                          onClick={async () => {
                            const recovery = resolvePastDueRecovery(subscriptionData);
                            if (recovery.kind === 'store_manage') {
                              window.open(recovery.url, '_blank', 'noopener,noreferrer');
                              return;
                            }
                            if (recovery.kind !== 'stripe_portal') {
                              setRenewalModalVariant('pix');
                              setIsPixRenewalModalOpen(true);
                              return;
                            }
                            try {
                              setLoadingManagePlan(true);
                              const url = await startStripeCustomerPortal(
                                window.location.href,
                                { preferOpenInvoice: true },
                              );
                              window.location.href = url;
                            } catch (error) {
                              console.error(error);
                              toast({
                                title: t('account.pastDue'),
                                description: t('billing.pastDuePortalError'),
                                variant: 'destructive',
                              });
                            } finally {
                              setLoadingManagePlan(false);
                            }
                          }}
                          disabled={loadingManagePlan}
                        >
                          {t('account.pastDuePay')}
                        </Button>
                      </div>
                    )}
                    {isCanceledPlan && subscriptionData?.cancellation_at && (
                      <div className="mt-3 p-1 border border-red-500 rounded-lg">
                        <p className="text-sm text-red-600 font-medium">
                          {t('account.subscriptionCanceled')}
                        </p>
                      </div>
                    )}
                    {storeProvider && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {t(`account.storeManaged.${storeProvider}`)}
                      </p>
                    )}
                    <PlanRenewalDate subscription={subscriptionData} className="mt-2 items-center" />
                    <RenewedCycleNotice subscription={subscriptionData} onStarted={refetch} className="mt-3" />
                    {subscriptionData?.payment_provider === "pagarme" && (subscriptionData?.next_payment_at || isCanceledPlan) && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          onClick={() => {
                            setRenewalModalVariant('pix');
                            setIsPixRenewalModalOpen(true);
                          }}
                        >
                          <QrCode className="w-4 h-4 mr-2" />
                          {t('account.renewPixSubscription')}
                        </Button>
                      </div>
                    )}
                    {isWalletGrantEligible && !storeProvider && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          onClick={() => {
                            setRenewalModalVariant('grant');
                            setIsPixRenewalModalOpen(true);
                          }}
                        >
                          <Wallet className="w-4 h-4 mr-2" />
                          {t('account.renewPlanNow')}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {myData?.myData?.onboarding_objective === 'other_channels' ? (
                      <>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-4 h-4" />
                              {t('account.curationsUsed')}
                            </span>
                            <span className="font-medium">
                              {subscriptionData?.current_month_library_curations || 0} / {(subscriptionData?.plan?.max_library_curations || 0) >= 1000000 ? t('common.unlimited') : subscriptionData?.plan?.max_library_curations || 0}
                            </span>
                          </div>
                          {(subscriptionData?.plan?.max_library_curations || 0) > 0 && (subscriptionData?.plan?.max_library_curations || 0) < 1000000 && (
                            <Progress
                              value={(subscriptionData?.current_month_library_curations || 0) / subscriptionData.plan.max_library_curations * 100}
                              className="h-2"
                            />
                          )}
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <FileOutput className="w-4 h-4" />
                              {t('account.clipsUsed')}
                            </span>
                            <span className="font-medium">
                              {subscriptionData?.current_month_library_exports || 0} / {(subscriptionData?.plan?.max_library_exports || 0) >= 1000000 ? t('common.unlimited') : subscriptionData?.plan?.max_library_exports || 0}
                            </span>
                          </div>
                          {(subscriptionData?.plan?.max_library_exports || 0) > 0 && (subscriptionData?.plan?.max_library_exports || 0) < 1000000 && (
                            <Progress
                              value={(subscriptionData?.current_month_library_exports || 0) / subscriptionData.plan.max_library_exports * 100}
                              className="h-2"
                            />
                          )}
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {t('account.processingTime')}
                            </span>
                            <span className="font-medium">
                              {formatHoursMinutes(subscriptionData?.current_month_library_video_seconds || 0)} / {formatHoursMinutes(subscriptionData?.plan?.max_library_video_seconds || 0)}
                            </span>
                          </div>
                          <Progress
                            value={subscriptionData?.current_month_library_video_seconds && subscriptionData?.plan?.max_library_video_seconds
                              ? (subscriptionData.current_month_library_video_seconds / subscriptionData.plan.max_library_video_seconds) * 100
                              : 0}
                            className="h-2"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {t('account.processingTime')}
                            </span>
                            <span className="font-medium">
                              {formatHoursMinutes(subscriptionData?.current_month_processing_seconds || 0)} / {formatHoursMinutes(subscriptionData?.plan?.processing_seconds || 36000)}
                            </span>
                          </div>
                          <Progress
                            value={subscriptionData?.current_month_processing_seconds && subscriptionData?.plan?.processing_seconds
                              ? (subscriptionData.current_month_processing_seconds / subscriptionData.plan.processing_seconds) * 100
                              : 0}
                            className="h-2"
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="flex items-center gap-1">
                              <Video className="w-4 h-4" />
                              {t('account.clipsUsed')}
                            </span>
                            <span className="font-medium">
                              {subscriptionData?.current_month_clips || 0} / {(subscriptionData?.plan?.max_clips || 0) >= 1000000 ? t('common.unlimited') : subscriptionData?.plan?.max_clips || 100}
                            </span>
                          </div>
                          <Progress
                            value={subscriptionData?.current_month_clips && subscriptionData?.plan?.max_clips
                              ? (subscriptionData.current_month_clips / subscriptionData.plan.max_clips) * 100
                              : 0}
                            className="h-2"
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {isFreePlan && (
                    <div className="flex">
                      <Button
                        variant="gradient"
                        className="w-full h-10"
                        size="sm"
                        onClick={() => {
                          onClose();
                          navigate('/plans');
                        }}
                      >
                        {t('account.upgradeToPro')}
                      </Button>
                    </div>
                  )}

                  {storeProvider && (
                    <Button
                      asChild
                      className="w-full h-10"
                      variant="outline"
                      size="sm"
                    >
                      <a
                        href={storeManageUrl(storeProvider, subscriptionData?.store_product_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('account.storeManaged.manage')}
                      </a>
                    </Button>
                  )}

                  {!isStripePlan && !isFreePlan && !storeProvider && (
                    <div className="flex justify-between gap-2">
                      <Button
                        className="flex-1 w-full h-10"
                        size="sm"
                        onClick={() => {
                          onClose();
                          navigate('/plans');
                        }}
                      >
                        {t('account.changePlan')}
                      </Button>
                      <Button
                        className="flex-1 w-full h-10"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsCancellationModalOpen(true)}
                      >
                        {t('account.cancelSubscription')}
                      </Button>
                    </div>
                  )}

                  {isStripePlan && (
                    <div className="flex justify-between gap-2">
                      {isCanceledPlan && (
                        <Button
                          variant="gradient"
                          className="w-full h-10"
                          size="sm"
                          onClick={() => {
                            onClose();
                            navigate('/plans');
                          }}
                        >
                          {t('account.reactivateSubscription')}
                        </Button>
                      )}
                      <Button
                        className="flex-1 w-full h-10"
                        variant="outline"
                        size="sm"
                        onClick={async() => {
                          try {
                            setLoadingManagePlan(true);
                            const response = await authAxios.request({
                              url: `/api/subscriptions/stripe/customer-portal`,
                              method: 'POST',
                              data: {
                                return_url: window.location.href,
                              },
                            })
                            window.location.href = response.data.url;
                          } catch (error) {
                            console.error(error);
                            toast({
                              title: t('account.profileUpdateError'),
                              description: t('account.profileUpdateErrorDesc'),
                              variant: "destructive",
                            });
                          } finally {
                            setLoadingManagePlan(false);
                          }
                        }}
                        disabled={loadingManagePlan || !isPlanMaster}
                      >
                        {loadingManagePlan ? t('account.loading') : t('account.manageSubscription')}
                      </Button>
                    </div>
                  )}

                  {myData?.myData?.onboarding_objective === 'other_channels' && (
                    <div className="border-t pt-4">
                      <p className="mb-3 text-sm text-muted-foreground">
                        {t('account.switchToOwnContent.hint')}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => setObjectiveSwitchTarget('own_brand')}
                      >
                        {t('account.switchToOwnContent.cta')}
                      </Button>
                    </div>
                  )}

                  {myData?.myData?.onboarding_objective === 'own_brand' && (
                    <div className="border-t pt-4">
                      <p className="mb-3 text-sm text-muted-foreground">
                        {t('account.switchToClippingChannels.hint')}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => setObjectiveSwitchTarget('other_channels')}
                      >
                        {t('account.switchToClippingChannels.cta')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Section */}
              <div className="relative">
                <Card className={!hasInvitationFeature ? "blur-[1px] opacity-20" : ""}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {t('account.usersOnPlan')}
                    </CardTitle>
                    {/* Invite button - shown for admins and master */}
                    {canInviteUsers(currentUserRole, isPlanMaster) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (hasInvitationFeature) {
                            setIsInviteModalOpen(true)
                          } else {
                            toast({
                              title: t('account.featureUnavailable'),
                              description: t('account.featureOnGoldPlan'),
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('account.invite')}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {subscriptionData?.users?.map((user: any, index: number) => {
                        const isMasterUser = user.email === subscriptionData?.master?.email;
                        const isCurrentUser = user.email === myData?.myData?.email;
                        const canEditRole = canManageRoles(currentUserRole, isPlanMaster) && !isMasterUser && !isCurrentUser;

                        return (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-gradient-primary text-primary-foreground text-sm">
                                {user.name ? getInitials(user.name) : "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm truncate">
                                  {user.name || t('account.loading')}
                                </p>
                                {isMasterUser && (
                                  <Badge variant="secondary" className="text-xs bg-primary/10 text-primary shrink-0">
                                    {t('account.master')}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground truncate">
                                {user.email}
                              </p>
                              {canEditRole ? (
                                <Select
                                  value={user.role || 'clip_only'}
                                  onValueChange={(value) => handleChangeUserRole(user.email, value)}
                                  disabled={updateTeamRoleLoading}
                                >
                                  <SelectTrigger className="h-7 text-xs w-[140px] mt-1" title={getRoleDescription(user.role || 'clip_only')}>
                                    <SelectValue placeholder="Selecione o cargo" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ROLES.map((role) => (
                                      <SelectItem
                                        key={role.value}
                                        value={role.value}
                                        className="cursor-pointer"
                                        title={role.description}
                                      >
                                        <span className="text-xs">{role.label}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <p className="text-xs text-muted-foreground" title={getRoleDescription(user.role || 'clip_only')}>
                                  {getRoleLabel(user.role || 'clip_only')}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Show remove button only for masters/admins, and only for other users (not themselves or the master) */}
                          {canManageRoles(currentUserRole, isPlanMaster) && !isCurrentUser && !isMasterUser && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setUserToRemove({ email: user.email, name: user.name || user.email });
                                setIsRemoveUserModalOpen(true);
                              }}
                              disabled={removeTeamLoading}
                              className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                            >
                              {removeTeamLoading ? (
                                <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      )})}
                    </div>

                    {/* Leave team button at bottom - for all users except master */}
                    {!isPlanMaster && (
                      <div className="mt-4 pt-4 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsLeaveTeamModalOpen(true)}
                          disabled={leaveTeamLoading}
                          className="w-full text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                        >
                          {leaveTeamLoading ? (
                            <div className="w-4 h-4 mr-1 animate-spin border-2 border-current border-t-transparent rounded-full" />
                          ) : (
                            <LogOut className="w-4 h-4 mr-1" />
                          )}
                          {t('account.leaveTeam')}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
                {!hasInvitationFeature && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded-lg">
                    <div className="text-center">
                      <Crown className="w-8 h-8 text-primary mx-auto mb-2" />
                      <p className="text-sm font-medium text-primary">
                        {t('account.userManagement')}
                        <br />
                        {t('account.availableOnGoldPlan')}
                        <br />
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <BotConnectionSettings />

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveProfile} disabled={isLoading || updateLoading}>
              {isLoading || updateLoading ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Invitation Modal - Outside main modal */}
    {hasInvitationFeature && canInviteUsers(currentUserRole, isPlanMaster) && (
      <Dialog open={isInviteModalOpen} onOpenChange={setIsInviteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('account.inviteUser')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invitation-link">{t('account.invitationLink')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="invitation-link"
                  value={`${window.location.origin}/invitation/${subscriptionData?.invitation_token || ''}`}
                  readOnly
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(`${window.location.origin}/invitation/${subscriptionData?.invitation_token || ''}`)}
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('account.shareLink')}
            </p>

            {/* Regenerate Token Button - Only for admin and master users */}
            {canManageRoles(currentUserRole, isPlanMaster) && (
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setIsRegenerateTokenModalOpen(true)}
                  disabled={refreshInvitationTokenLoading}
                >
                  {refreshInvitationTokenLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {t('account.regenerateLink')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* Regenerate Invitation Token Confirmation Modal */}
    <AlertDialog open={isRegenerateTokenModalOpen} onOpenChange={setIsRegenerateTokenModalOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('account.regenerateLinkTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="text-yellow-500 font-semibold">{t('account.regenerateLinkWarning')}</span> {t('account.regenerateLinkDesc')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={refreshInvitationTokenLoading}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRegenerateInvitationToken}
            disabled={refreshInvitationTokenLoading}
          >
            {refreshInvitationTokenLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {t('account.regenerating')}
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.confirm')}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Profile Picture Modal */}
    <Dialog open={isProfilePictureModalOpen} onOpenChange={handleCloseProfilePictureModal}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>{t('account.changeProfilePicture')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Preview */}
          <div className="flex justify-center">
            <div className="relative">
              <Avatar className="w-32 h-32">
                <AvatarImage src={previewUrl} />
                <AvatarFallback className="bg-gradient-primary text-primary-foreground">
                  <ImageIcon className="w-16 h-16" />
                </AvatarFallback>
              </Avatar>
            </div>
          </div>

          {/* File info */}
          {selectedFile && (
            <div className="text-center text-sm text-muted-foreground">
              <p>{t('account.file')} {selectedFile.name}</p>
            </div>
          )}

          {/* Error display */}
          {uploadError && (
            <div className="text-sm text-red-500 text-center">
              {uploadError}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between gap-3">
            <Button
              variant="outline"
              onClick={handleCloseProfilePictureModal}
              disabled={isUploading || isChangingProfilePicture}
            >
              {t('account.cancel')}
            </Button>
            <Button
              onClick={handleProfilePictureUpload}
              disabled={!selectedFile || isUploading || isChangingProfilePicture}
            >
              {isUploading || isChangingProfilePicture ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-pulse" />
                  {t('account.uploading')}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {t('account.confirm')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Cancellation Modal */}
    <Dialog open={isCancellationModalOpen} onOpenChange={setIsCancellationModalOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('account.cancelSubscriptionTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cancellation-reason">{t('account.whyCanceling')}</Label>
              <Select
                value={cancellationData.reason}
                onValueChange={(value) => handleCancellationChange("reason", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('account.selectReason')} />
                </SelectTrigger>
                <SelectContent>
                  {CANCELLATION_REASON_KEYS.map((reasonKey) => (
                    <SelectItem key={reasonKey} value={reasonKey}>
                      {t(`account.cancellationReasons.${reasonKey}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {cancellationData.reason === "other" && (
              <div className="space-y-2">
                <Label htmlFor="other-reason">{t('account.whatReason')}</Label>
                <Textarea
                  id="other-reason"
                  placeholder={t('account.describeReason')}
                  value={cancellationData.customReason}
                  onChange={(e) => handleCancellationChange("customReason", e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
            )}

            {cancellationData.reason && (
              <div className="space-y-2">
                <Label htmlFor="feedback">{t('account.couldWeHaveDoneSomething')}</Label>
                <Textarea
                  id="feedback"
                  placeholder={t('account.shareFeedback')}
                  value={cancellationData.feedback}
                  onChange={(e) => handleCancellationChange("feedback", e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsCancellationModalOpen(false);
                setCancellationData({ reason: "", customReason: "", feedback: "" });
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCancellationSubmit}
              disabled={cancellationLoading ||
                       !cancellationData.reason ||
                       !cancellationData.feedback ||
                       (cancellationData.reason === "other" && !cancellationData.customReason)}
            >
              {cancellationLoading ? t('account.sending') : t('account.sendAndContinue')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <ObjectiveSwitchConfirmDialog
      to={objectiveSwitchTarget}
      clipperMonthlyClipLimit={resolveClipperMonthlyClipLimit(
        subscriptionData?.plan?.max_library_exports,
      )}
      loading={objectiveSwitchLoading}
      onCancel={() => setObjectiveSwitchTarget(null)}
      onConfirm={() => {
        if (objectiveSwitchTarget) {
          void handleObjectiveSwitch(objectiveSwitchTarget);
        }
      }}
    />

    {/* Leave Team Confirmation Modal */}
    <Dialog open={isLeaveTeamModalOpen} onOpenChange={setIsLeaveTeamModalOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('account.leaveTeamTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('account.leaveTeamConfirm')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsLeaveTeamModalOpen(false)}
              disabled={leaveTeamLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await handleLeaveTeam();
                setIsLeaveTeamModalOpen(false);
              }}
              disabled={leaveTeamLoading}
            >
              {leaveTeamLoading ? (
                <>
                  <div className="w-4 h-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  {t('account.leaving')}
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('account.confirmLeave')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Remove User Confirmation Modal */}
    <Dialog open={isRemoveUserModalOpen} onOpenChange={(open) => {
      setIsRemoveUserModalOpen(open);
      if (!open) setUserToRemove(null);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('account.removeUser')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('account.removeUserConfirm', { name: userToRemove?.name }) }} />
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsRemoveUserModalOpen(false);
                setUserToRemove(null);
              }}
              disabled={removeTeamLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (userToRemove) {
                  await handleRemoveUser(userToRemove.email);
                  setIsRemoveUserModalOpen(false);
                  setUserToRemove(null);
                }
              }}
              disabled={removeTeamLoading}
            >
              {removeTeamLoading ? (
                <>
                  <div className="w-4 h-4 mr-2 animate-spin border-2 border-current border-t-transparent rounded-full" />
                  {t('account.removing')}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('account.confirmRemoval')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog
      open={!!disconnectTarget}
      onOpenChange={(open) => {
        if (!open) setDisconnectTarget(null);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("account.disconnectSocialTitle")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("account.disconnectSocialDescription", {
            account: disconnectTarget ? socialAccountName(disconnectTarget) : "",
          })}
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => setDisconnectTarget(null)}
            disabled={disconnectingSocial}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDisconnectSocial()}
            disabled={disconnectingSocial}
          >
            {t("account.disconnectSocial")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* PIX Renewal Modal */}
    <PixRenewalModal
      open={isPixRenewalModalOpen}
      onOpenChange={setIsPixRenewalModalOpen}
      onSuccess={async () => {
        await refetch();
      }}
      autoGenerate={renewalModalVariant === 'pix'}
      variant={renewalModalVariant}
      onCouponApplied={refetch}
    />
    </>
  );
};

export default AccountSettings;
